import { Fragment } from "react"
import { ASCII } from "@/content/ascii"
import { colors } from "@/theme/colors"
import type { AsciiName } from "@/content/ascii"
import type { FamilyNumber } from "@/theme/colors"
import styles from "@/styles/components/AsciiArt.module.css"

/**
 * One named motif from `content/ascii.ts`, set in the mono face.
 *
 * Decorative in every place it appears — a customer card already says which
 * customer it is, a curtain already says which round — so the whole thing is
 * `aria-hidden` and no motif carries a label.
 *
 * The head lines take the family's bloom and the rest a constant leaf green.
 * That split is the point: it is what makes a grid of these read as one drawing
 * in several colours rather than as several unrelated drawings.
 */

interface AsciiArtProps {
  motif: AsciiName
  /** The accent for the head lines. Defaults to the leaf green, i.e. no accent. */
  family?: FamilyNumber
  size?: "sm" | "md" | "lg"
  /** The muted ink used on curtains and the job gate, in place of both colours. */
  muted?: boolean
  className?: string
}

const LEAF = colors.family[5].bloom
/** The one card in the deck that is still blank. */
const MARK = "*"

export default function AsciiArt({
  motif,
  family,
  size = "md",
  muted = false,
  className,
}: AsciiArtProps) {
  const { lines, head } = ASCII[motif]
  const accent = family ? colors.family[family].bloom : LEAF

  return (
    <pre
      className={[styles.art, styles[size], muted ? styles.muted : "", className]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true"
    >
      {lines.map((line, index) => (
        <span
          key={index}
          className={styles.line}
          style={muted ? undefined : { color: index < head ? accent : LEAF }}
        >
          {renderLine(line)}
          {"\n"}
        </span>
      ))}
    </pre>
  )
}

/*
 * The blank card's asterisk is picked out whatever the rest of the motif is
 * doing. It is the only character in the system that carries meaning on its own:
 * on the curtain and the job gate it is the slot the room has not filled yet.
 */
function renderLine(line: string) {
  if (!line) return " "
  if (!line.includes(MARK)) return line

  return line.split(MARK).map((part, index, parts) => (
    <Fragment key={index}>
      {part}
      {index < parts.length - 1 ? <span className={styles.mark}>{MARK}</span> : null}
    </Fragment>
  ))
}
